import React, { useCallback, useContext, useMemo } from 'react';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { InfluencerPlanContext } from '../../../InfluencerPlan/contexts/InfluencerPlanContext';
import { Button, FormField, Label, Modal } from '@round/ui-kit';
import TextArea from 'SharedComponents/Forms/TextArea/TextArea';
import { showNotification } from 'helpers';
import styles from './EmailConfiguration.module.css';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { useMicroCampaigns } from '../contexts/MicroCampaign';
import { microwave } from '@round/api';

type FormValues = {
    brief: string;
    micros_payment_request_url: string;
    payment_request_deadline_in_days: number;
};

const defaultDeadlineDays = 2;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    tiktokCampaign?: microwave.Campaign;
};

const EmailConfiguration = ({ isOpen, onClose, tiktokCampaign }: Props) => {
    const { influencerPlan, updateInfluencerPlan } = useContext(InfluencerPlanContext);
    const { update: updateMicroCampaign } = useMicroCampaigns();

    const initialValues: FormValues = useMemo(
        () => ({
            brief: tiktokCampaign?.brief ?? '',
            micros_payment_request_url: influencerPlan?.micros_payment_request_url ?? '',
            payment_request_deadline_in_days: tiktokCampaign?.payment_request_deadline_in_days ?? defaultDeadlineDays,
        }),
        [
            tiktokCampaign?.brief,
            tiktokCampaign?.payment_request_deadline_in_days,
            influencerPlan?.micros_payment_request_url,
        ]
    );

    const handeSubmit = useCallback(
        async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
            if (!influencerPlan || !updateInfluencerPlan) {
                return;
            }

            try {
                helpers.setSubmitting(true);
                const response = await updateInfluencerPlan({
                    micros_payment_request_url: values.micros_payment_request_url,
                });

                if (response.status === 404 || response.status === 403) {
                    showNotification(response.data.detail, 'error');
                    return;
                }

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                if (tiktokCampaign) {
                    const campaignResponse = await updateMicroCampaign(tiktokCampaign.id, {
                        brief: values.brief,
                        payment_request_deadline_in_days: values.payment_request_deadline_in_days,
                    });
                    if (campaignResponse.status !== 200) {
                        showNotification('Could not update campaign', 'error');
                        return;
                    }
                }

                showNotification('Saved!', 'info');
            } catch (e) {
                showNotification('Could not update email configuration', 'error');
            } finally {
                helpers.setSubmitting(false);
            }
        },
        [influencerPlan, tiktokCampaign, updateInfluencerPlan, updateMicroCampaign]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose} className={styles.container}>
            <h3 className={styles.title}>Email configuration</h3>
            <Formik initialValues={initialValues} onSubmit={handeSubmit}>
                {({ isSubmitting, dirty }) => (
                    <Form className={styles.form}>
                        <FormField>
                            <Label htmlFor="micros_payment_request_url">Payment request URL</Label>
                            <Field
                                name="micros_payment_request_url"
                                id="micros_payment_request_url"
                                placeholder="Enter request url..."
                            />
                            <ErrorMessage name="micros_payment_request_url">
                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="payment_request_deadline_in_days">Deadline after (days)</Label>
                            <Field
                                type="number"
                                id="payment_request_deadline_in_days"
                                name="payment_request_deadline_in_days"
                                placeholder="Enter number of days..."
                            />
                            <ErrorMessage name="payment_request_deadline_in_days">
                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="brief">Brief</Label>
                            <TextArea
                                name="brief"
                                id="brief"
                                placeholder={tiktokCampaign ? 'Enter brief...' : 'Create a TikTok campaign first'}
                                className={styles.briefField}
                                disabled={!tiktokCampaign}
                            />
                            <ErrorMessage name="brief">
                                {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>
                        <div>
                            <Button
                                type="filled"
                                color="black"
                                disabled={!dirty || !influencerPlan}
                                loading={isSubmitting}
                            >
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default EmailConfiguration;
